import { useState } from 'react';
import { Card, Button, Input, Select, IconButton } from '../ui';
import { DocumentTextIcon, TrashIcon, PencilIcon, CheckIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useAddDocument, useUpdateDocument, useDeleteDocument } from '../../hooks/useClients';

const CATEGORIES = [
  { value: 'deliverable',   label: 'Deliverable' },
  { value: 'meeting_notes', label: 'Meeting Notes' },
  { value: 'report',        label: 'Report' },
  { value: 'template',      label: 'Template' },
  { value: 'internal',      label: 'Internal (consultant only)' },
];

const CATEGORY_BADGE = {
  deliverable:   'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  meeting_notes: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  report:        'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  template:      'bg-border-light text-text-muted-light dark:bg-surface-dark dark:text-text-muted',
  internal:      'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
};

function formatDate(isoString) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function DocumentLibrary({ documents, clientId }) {
  const addDocument = useAddDocument();
  const updateDocument = useUpdateDocument();
  const deleteDocument = useDeleteDocument();

  const [filterCategory, setFilterCategory] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'deliverable', url: '', notes: '' });
  const [formError, setFormError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const filtered = filterCategory === 'all'
    ? documents
    : documents.filter((d) => d.category === filterCategory);

  const handleAdd = async () => {
    setFormError('');
    if (!form.name.trim()) { setFormError('Document name is required.'); return; }
    if (!form.url.trim()) { setFormError('URL is required.'); return; }
    await addDocument.mutateAsync({ clientId, ...form });
    setForm({ name: '', category: 'deliverable', url: '', notes: '' });
    setShowForm(false);
  };

  const handleDelete = async (docId) => {
    setDeletingId(docId);
    await deleteDocument.mutateAsync({ clientId, docId });
    setDeletingId(null);
  };

  const startEdit = (doc) => {
    setEditingId(doc.id);
    setEditForm({ name: doc.name, category: doc.category, url: doc.url, notes: doc.notes || '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSaveEdit = async (docId) => {
    if (!editForm.name?.trim() || !editForm.url?.trim()) return;
    await updateDocument.mutateAsync({ clientId, docId, ...editForm });
    setEditingId(null);
    setEditForm({});
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div className="flex items-center gap-2">
          <Select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            options={[{ value: 'all', label: 'All categories' }, ...CATEGORIES]}
          />
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowForm((v) => !v)}>
          <PlusIcon className="w-4 h-4 mr-1 inline-block" />
          {showForm ? 'Cancel' : 'Add Document'}
        </Button>
      </div>

      {/* Add form */}
      {showForm && (
        <Card className="p-4">
          <h4 className="font-medium text-text-primary-light dark:text-text-primary mb-3">Add Document</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-text-muted-light dark:text-text-muted mb-1">Name *</label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="AI Readiness Report v2" />
            </div>
            <div>
              <label className="block text-xs text-text-muted-light dark:text-text-muted mb-1">Category</label>
              <Select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                options={CATEGORIES}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-text-muted-light dark:text-text-muted mb-1">URL / Link *</label>
              <Input value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://drive.google.com/…" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-text-muted-light dark:text-text-muted mb-1">Notes</label>
              <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Optional notes…" />
            </div>
          </div>
          {formError && <p className="mt-2 text-sm text-danger">{formError}</p>}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="secondary" size="sm" onClick={() => { setShowForm(false); setFormError(''); }}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleAdd} disabled={addDocument.isPending}>
              {addDocument.isPending ? 'Adding…' : 'Add Document'}
            </Button>
          </div>
        </Card>
      )}

      {/* Document list */}
      {filtered.length === 0 ? (
        <div className="text-center py-10 text-text-muted-light dark:text-text-muted">
          <DocumentTextIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No documents yet.</p>
        </div>
      ) : (
        <Card padding="none" className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-light dark:bg-surface-dark">
              <tr className="text-left text-xs text-text-muted-light dark:text-text-muted">
                <th className="px-4 py-2.5 font-medium">Document</th>
                <th className="px-4 py-2.5 font-medium">Category</th>
                <th className="px-4 py-2.5 font-medium hidden md:table-cell">Notes</th>
                <th className="px-4 py-2.5 font-medium hidden sm:table-cell">Uploaded</th>
                <th className="px-4 py-2.5 font-medium hidden sm:table-cell">By</th>
                <th className="px-4 py-2.5 font-medium w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light dark:divide-border">
              {filtered.map((doc) => {
                const isEditing = editingId === doc.id;
                return isEditing ? (
                  <tr key={doc.id} className="bg-background-light dark:bg-surface-dark">
                    <td className="px-4 py-2">
                      <div className="space-y-1.5">
                        <Input
                          value={editForm.name}
                          onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                          placeholder="Document name"
                          autoFocus
                        />
                        <Input
                          value={editForm.url}
                          onChange={(e) => setEditForm((f) => ({ ...f, url: e.target.value }))}
                          placeholder="https://…"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <Select
                        value={editForm.category}
                        onChange={(e) => setEditForm((f) => ({ ...f, category: e.target.value }))}
                        options={CATEGORIES}
                      />
                    </td>
                    <td className="px-4 py-2 hidden md:table-cell">
                      <Input
                        value={editForm.notes}
                        onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                        placeholder="Notes…"
                      />
                    </td>
                    <td className="px-4 py-2 hidden sm:table-cell" />
                    <td className="px-4 py-2 hidden sm:table-cell" />
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <IconButton
                          icon={CheckIcon}
                          label="Save"
                          variant="ghost"
                          size="sm"
                          disabled={updateDocument.isPending}
                          onClick={() => handleSaveEdit(doc.id)}
                          className="text-green-600 hover:text-green-700"
                        />
                        <IconButton
                          icon={XMarkIcon}
                          label="Cancel"
                          variant="ghost"
                          size="sm"
                          onClick={cancelEdit}
                        />
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr
                    key={doc.id}
                    className="cursor-pointer hover:opacity-75 transition"
                    onClick={() => window.open(doc.url, '_blank', 'noopener,noreferrer')}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <DocumentTextIcon className="w-4 h-4 text-text-muted-light dark:text-text-muted flex-shrink-0" />
                        <span className="font-medium text-text-primary-light dark:text-text-primary">{doc.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] px-1.5 py-0 rounded font-medium ${CATEGORY_BADGE[doc.category] || CATEGORY_BADGE.template}`}>
                        {CATEGORIES.find((c) => c.value === doc.category)?.label || doc.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-text-secondary-light dark:text-text-secondary hidden md:table-cell max-w-[200px] truncate">
                      {doc.notes || '—'}
                    </td>
                    <td className="px-4 py-3 text-text-secondary-light dark:text-text-secondary hidden sm:table-cell whitespace-nowrap">
                      {formatDate(doc.uploadedAt)}
                    </td>
                    <td className="px-4 py-3 text-text-secondary-light dark:text-text-secondary hidden sm:table-cell">
                      {doc.uploadedBy?.split('@')[0] || '—'}
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <IconButton
                          icon={PencilIcon}
                          label="Edit"
                          variant="ghost"
                          size="sm"
                          onClick={() => startEdit(doc)}
                        />
                        <IconButton
                          icon={TrashIcon}
                          label="Delete"
                          variant="ghost"
                          size="sm"
                          disabled={deletingId === doc.id}
                          onClick={() => handleDelete(doc.id)}
                          className="text-danger hover:text-danger/80"
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
